import supabase from '@/utils/supabase';
import { type LoaderFunctionArgs, useLoaderData } from 'react-router';
import type { Database } from '@/types/database.types';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import StatusBadge from '@/components/StatusBadge';
import { getProjectTeamStatus, getTeamStatusDisplay } from '@/utils/status';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsTrigger, TabsContent, TabsList } from '@/components/ui/tabs';

import OverviewTab from './OverviewTab';
import DetailsTab from './DetailsTab';
import VideosTab from './VideosTab';
import MediaTab from './MediaTab';
import ClientTab from './ClientTab';
import TeamTab from './TeamTab';
import InvoiceTab from './InvoiceTab';
import SettingsTab from './SettingsTab';

type Project = Database['public']['Tables']['projects']['Row'];
type ProjectTeam = Database['public']['Tables']['projects_team']['Row'];
type Deliverable = Database['public']['Tables']['deliverables']['Row'];
type DeliverableTeam = Database['public']['Tables']['deliverables_team']['Row'];
type Upload = Database['public']['Tables']['uploads']['Row'];
type Member = Database['public']['Tables']['project_members']['Row'];

type ProjectWithTeamAndMembers = Project & {
  projects_team: ProjectTeam;
} & { project_members: Member[] | null };
type DeliverableWithTeam = Deliverable & {
  deliverables_team: DeliverableTeam;
};

type Loader = {
  project: ProjectWithTeamAndMembers;
  deliverables: DeliverableWithTeam[];
  uploads: Upload[];
};

export async function loader({ params }: LoaderFunctionArgs) {
  const { projectId } = params;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  if (!projectId || !userId) {
    throw new Response('Unauthorised', { status: 401 });
  }

  const [projectResult, deliverableResult, uploadResult] = await Promise.all([
    supabase
      .from('projects')
      .select('*, projects_team(*), project_members(*)')
      .eq('id', projectId)
      .single(),
    supabase
      .from('deliverables')
      .select('*, deliverables_team(*)')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true }),
    supabase
      .from('uploads')
      .select()
      .eq('project_id', projectId)
      .eq('upload_type', 'media')
      .order('created_at', { ascending: true }),
  ]);

  const { data: project, error: projectError } = projectResult;
  const { data: deliverables, error: deliverablesError } = deliverableResult;
  const { data: uploads, error: uploadsError } = uploadResult;

  if (projectError || !project) {
    throw new Response('Failed to load project', { status: 500 });
  }

  if (deliverablesError || !deliverables) {
    throw new Response('Failed to load deliverables', { status: 500 });
  }

  if (uploadsError || !uploads) {
    throw new Response('Failed to load uploads', { status: 500 });
  }

  return {
    project,
    deliverables,
    uploads,
  };
}

export default function EngineProjectDetails() {
  const { project, deliverables, uploads } = useLoaderData() as Loader;

  useEffect(() => {
    if (
      !project.frame_io_project_id ||
      !project.frame_io_client_folder_id ||
      !project.frame_io_client_review_link_id ||
      !project.projects_team.frame_io_team_folder_id ||
      !project.projects_team.frame_io_team_review_link_id
    ) {
      (async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();

          const response = await fetch(
            `/api/engine/projects/${project.id}/setup-frameio`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session?.access_token}`,
              },
            }
          );
          if (!response.ok) {
            let body: unknown;
            try {
              body = await response.json();
            } catch {
              body = await response.text().catch(() => '<unreadable>');
            }
            const error = new Error(
              `Frame.io setup request failed: ${response.status} ${response.statusText}`
            );
            Sentry.captureException(error, {
              tags: { component: 'frameio', op: 'setup-trigger' },
              extra: {
                projectId: project.id,
                status: response.status,
                statusText: response.statusText,
                body,
              },
            });
            console.error('frameio setup failed:', response.status, body);
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'frameio', op: 'setup-trigger' },
            extra: { projectId: project.id, kind: 'network' },
          });
          console.error('Frame.io setup network error:', error);
        }
      })();
    }
  }, [
    project.id,
    project.frame_io_project_id,
    project.frame_io_client_folder_id,
    project.frame_io_client_review_link_id,
    project.projects_team.frame_io_team_folder_id,
    project.projects_team.frame_io_team_review_link_id,
  ]);

  const projectStatusCode = getProjectTeamStatus(
    deliverables.map(
      (d: DeliverableWithTeam) => d.deliverables_team?.team_status
    ),
    'producer'
  );
  const { label, colour } = getTeamStatusDisplay(projectStatusCode, 'producer');

  return (
    <>
      <header>
        <div className="flex flex-row gap-4 items-center mb-5">
          <StatusBadge label={label} colour={colour} />
          <Separator orientation="vertical" className="h-6! w-0.5!" />
          <h1 className="text-3xl font-bold">{project.project_name}</h1>
        </div>
      </header>
      <div className="grid">
        <div className="max-w-300">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList variant={'line'} className="mb-4">
              <TabsTrigger value="overview" className="px-6 cursor-pointer">
                Overview
              </TabsTrigger>
              <TabsTrigger value="details" className="px-6 cursor-pointer">
                Details
              </TabsTrigger>
              <TabsTrigger value="videos" className="px-6 cursor-pointer">
                Videos
              </TabsTrigger>
              <TabsTrigger value="media" className="px-6 cursor-pointer">
                Media
              </TabsTrigger>
              <TabsTrigger value="client" className="px-6 cursor-pointer">
                Client
              </TabsTrigger>
              <TabsTrigger value="team" className="px-6 cursor-pointer">
                Team
              </TabsTrigger>
              <TabsTrigger value="invoice" className="px-6 cursor-pointer">
                Invoice
              </TabsTrigger>
              <TabsTrigger value="settings" className="px-6 cursor-pointer">
                Settings
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
              <OverviewTab />
            </TabsContent>
            <TabsContent value="details">
              <DetailsTab project={project} />
            </TabsContent>
            <TabsContent value="videos">
              <VideosTab deliverables={deliverables} project={project} />
            </TabsContent>
            <TabsContent value="media">
              <MediaTab uploads={uploads} projectId={project.id} />
            </TabsContent>
            <TabsContent value="client">
              <ClientTab />
            </TabsContent>
            <TabsContent value="team">
              <TeamTab />
            </TabsContent>
            <TabsContent value="invoice">
              <InvoiceTab />
            </TabsContent>
            <TabsContent value="settings">
              <SettingsTab project={project} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
